import React, { useEffect, useState } from 'react';
import Feature from '../feature/Feature';
import { type paths } from '../../../../endpoint/Schema';
import { Icons } from '../../../../assets/icons';
import FAQEditQuestion from './FAQEditQuestion';
import FAQTopicQuestions from './FAQTopicQuestions';
import useFAQStore from '../../../../stores/FAQStore';

interface IFAQFeatureProps {
  featureSettings:
    | paths['/api/v2/admin/feature-setting']['get']['responses']['200']['content']['application/json']
    | undefined;
  setFeatureSettings: React.Dispatch<
    React.SetStateAction<
      | paths['/api/v2/admin/feature-setting']['get']['responses']['200']['content']['application/json']
      | undefined
    >
  >;
}

export default function FAQFeature({
  featureSettings,
  setFeatureSettings,
}: IFAQFeatureProps) {
  const { fetchTopics } = useFAQStore();
  const topics = useFAQStore((state) => state.topics).filter(
    (topic) => topic.enabled,
  );
  const [addingQuestion, setAddingQuestion] = useState(false);

  useEffect(() => {
    void fetchTopics();
  }, [fetchTopics]);

  function handleToggleChecked(checked: boolean) {
    setFeatureSettings((prev) => ({
      ...prev,
      faqSettings: { ...prev?.faqSettings, enabled: checked },
    }));
  }

  function onAddQuestion() {
    setAddingQuestion(true);
  }

  function onCancelAddingQuestion() {
    setAddingQuestion(false);
  }

  return (
    <Feature
      icon={Icons.ic_faq}
      image="img_faq"
      title="FAQ"
      description="The FAQ feature serves as a comprehensive resource for users to find answers to common questions and inquiries related to the app. Users can easily access a curated list of frequently asked questions, covering topics such as app usage, features, troubleshooting, and more. This feature helps users quickly resolve any queries they may have, enhancing their overall app experience."
      checked={featureSettings?.faqSettings?.enabled ?? false}
      onChange={handleToggleChecked}
    >
      <div className="max-md:max-w-full">
        <div className="w-full px-20 pb-10 transition-all">
          {topics.length > 0 && (
            <div className="flex flex-col p-5 mt-6 rounded-xl bg-whiteSmoke">
              {topics.filter((topic) => topic.faq && topic.faq?.length > 0)
                .length > 0 && (
                <div className="flex  gap-4 items-center text-2xl mb-1">
                  Topics of your questions
                </div>
              )}
              {topics
                .filter((topic) => topic.faq && topic.faq?.length > 0)
                .map((topic) => (
                  <FAQTopicQuestions
                    key={`faq_topic_${topic.id}`}
                    topicId={topic.id}
                  />
                ))}

              {!addingQuestion && (
                <button
                  className="flex flex-row self-start gap-4 items-center text-3xl my-6"
                  onClick={onAddQuestion}
                >
                  <img src={Icons.ic_add_fill} className="w-9" />
                  <div className="mt-2">Add more</div>
                </button>
              )}
            </div>
          )}

          {(addingQuestion || topics.length === 0) && (
            <FAQEditQuestion
              onSave={onCancelAddingQuestion}
              onCancel={onCancelAddingQuestion}
            />
          )}
        </div>
      </div>
    </Feature>
  );
}
